<script setup lang="ts">
const debug = useDebug();
const props = withDefaults(
  defineProps<{
    progress?: number;
    lengthSeconds?: number;
    shortMode?: boolean;
    darkMode?: boolean;
  }>(),
  {
    progress: 0,
    lengthSeconds: 1,
    previewLengthSeconds: 1,

    darkMode: false,
    shortMode: false,
  },
);

const emits = defineEmits<{
  (e: "dragstart", time: number): void;
  (e: "dragmove", time: number): void;
  (e: "dragend", time: number): void;
}>();

watch(
  props,
  (newProps) => {
    if (!dragging) {
      progressVal.value = newProps.progress;
    }
  },
  { deep: true },
);

const formattedProgress = computed(() => formatTime(progressVal.value));
const formattedMaxTime = computed(() => formatTime(props.lengthSeconds));
const bar = ref<HTMLElement | null>(null);
const progressVal = ref(0);
const progressPixels = computed(() => {
  const len = props.lengthSeconds;
  const percent = Math.min(1, progressVal.value / len);
  let px = 0;
  if (bar.value && (bar.value as HTMLElement).getBoundingClientRect) {
    const bounds = (bar.value as HTMLElement).getBoundingClientRect();
    px = bounds.width * percent;
  }
  return px;
});
let dragging = false;

function onMouseDown(e: MouseEvent) {
  getNewProgresFromMouseLocation(e);
  dragging = true;
  onMouseMove(e);
  emits("dragstart", progressVal.value);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onMouseUp);
}

function onMouseUp() {
  debug.log("on drag end");
  dragging = false;
  emits("dragend", progressVal.value);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onMouseUp);
}

function onMouseMove(e: MouseEvent) {
  getNewProgresFromMouseLocation(e);
  emits("dragmove", progressVal.value);
  e.stopPropagation();
  e.preventDefault();
  e.cancelBubble = true;
  return false;
}

function getNewProgresFromMouseLocation(e: MouseEvent) {
  const len = props.lengthSeconds;
  const bounds = (bar.value as HTMLElement).getBoundingClientRect();
  progressVal.value =
    (Math.min(Math.max(e.clientX - bounds.left, 0), bounds.width) /
      bounds.width) *
    len;
}

function formatTime(timeSeconds: number): string {
  if (!timeSeconds || isNaN(timeSeconds)) {
    return "0:00";
  }
  const mins = Math.floor(timeSeconds / 60).toString();
  const seconds = Math.round(timeSeconds % 60)
    .toString()
    .padStart(2, "0");
  const formatted = `${mins}:${seconds}`;
  return formatted;
}
</script>

<template>
  <div
    class="progress-bar-wrapper"
    :class="{ 'dark-mode': darkMode }"
  >
    <div
      v-if="!shortMode"
      class="display-timing"
    >
      {{ formattedProgress }}
    </div>
    <div class="progress-bar-line-wrapper">
      <div
        class="progress-bar"
        @mousedown="onMouseDown"
        @mouseup="onMouseUp"
      >
        <div class="bar-wrapper">
          <div
            ref="bar"
            class="bar"
          ></div>
        </div>
        <div
          class="bar-active"
          :style="`width: ${progressPixels}px`"
        ></div>
        <button
          type="button"
          class="dot"
          :style="`transform: translateX(${progressPixels}px)`"
        ></button>
      </div>
    </div>

    <div
      v-if="shortMode"
      class="display-timing"
    >
      {{ formattedProgress }}
    </div>
    <div
      v-else-if="!shortMode && lengthSeconds"
      class="display-timing"
    >
      {{ formattedMaxTime }}
    </div>
  </div>
</template>

<style scoped lang="postcss">
.progress-bar-wrapper {
  --bar-height: 4px;
  --dot-size: 15px;
  --half-bar-height: calc(var(--bar-height) / 2);
  --half-dot-size: calc(var(--dot-size) / 2);
  --radius: calc(var(--bar-height) / 2);

  /* --color-bar: theme(colors.gray.400);*/
  /* --color-dot: theme(colors.primary);*/
  /* --color-accent: theme(colors.primary);*/
  --color-bar: theme(colors.white);
  --color-dot: theme(colors.primary);
  --color-accent: theme(colors.primary);
  --color-text: theme(colors.white);

  @apply flex flex-1 items-center gap-5;
  color: var(--color-text);
}
.progress-bar-wrapper.dark-mode {
  --color-bar: theme(colors.gray.200);
  --color-text: theme(colors.gray.800);
  color: var(--color-text);
}

.progress-bar {
  @apply relative z-10 mx-0 my-2 flex flex-grow cursor-grab items-center justify-center overflow-visible;
  height: 1.5em;
  &.preview-mode {
    @apply left-[15%] w-[25%];
  }
}
.progress-bar-line-wrapper {
  @apply relative flex-1;
}
.bar-wrapper {
  @apply relative flex w-full items-center;
}
.bar {
  @apply box-border h-0 flex-grow;
  border-bottom: var(--bar-height) solid var(--color-bar);
  border-radius: var(--radius);
}

.bar-active {
  @apply absolute left-0;
  border-radius: var(--radius);
  top: calc(1.5em / 2 - var(--half-bar-height));
  height: var(--bar-height);
  background: var(--color-accent);
}

.dot {
  @apply pointer-events-none absolute inline-block border-0;
  left: calc(-1 * var(--half-dot-size));
  top: calc(1.5em / 2 - var(--half-dot-size));
  border-radius: 50%;
  height: var(--dot-size);
  width: var(--dot-size);
  background: var(--color-dot);
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.25);
}

.display-timing {
  font-size: 0.8em;
}

.sign-in {
  @apply absolute;
  margin-top: 0.25em;
  font-size: 0.8em;
}
</style>
